"use client";

// Seek bar: thin track, hover-reveal thumb, grows while dragging, a big
// invisible touch target, and a time tooltip following the pointer.
// Seeks on release (not on every move) so audio never stutters while scrubbing.
// Always LTR — time flows left→right regardless of page direction.

import { useRef, useState } from "react";
import { useLocale } from "next-intl";

import { formatTime } from "@/lib/formatTime";

type Props = {
  value: number;
  duration: number;
  disabled?: boolean;
  label: string;
  onSeek: (seconds: number) => void;
};

const KEY_STEP = 5;
const KEY_BIG_STEP = 30;

export default function Scrubber({ value, duration, disabled, label, onSeek }: Props) {
  const locale = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragFrac, setDragFrac] = useState(0);
  const [hoverFrac, setHoverFrac] = useState<number | null>(null);

  const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
  const fracAt = (clientX: number) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || r.width === 0) return 0;
    return clamp01((clientX - r.left) / r.width);
  };

  const playedFrac = duration ? clamp01(value / duration) : 0;
  const shownFrac = dragging ? dragFrac : playedFrac;
  const tipFrac = dragging ? dragFrac : hoverFrac;
  const active = dragging; // data flag for CSS

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled || !duration) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setDragFrac(fracAt(e.clientX));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (disabled || !duration) return;
    const f = fracAt(e.clientX);
    setHoverFrac(f);
    if (dragging) setDragFrac(f);
  };
  const finish = (e: React.PointerEvent, commit: boolean) => {
    if (!dragging) return;
    setDragging(false);
    if (commit) onSeek(fracAt(e.clientX) * duration);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || !duration) return;
    const step = e.shiftKey ? KEY_BIG_STEP : KEY_STEP;
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") next = value + step;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = value - step;
    else if (e.key === "PageUp") next = value + KEY_BIG_STEP;
    else if (e.key === "PageDown") next = value - KEY_BIG_STEP;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = duration;
    if (next === null) return;
    e.preventDefault();
    onSeek(Math.min(duration, Math.max(0, next)));
  };

  // Keep the tooltip fully inside the bar near the edges.
  const tipLeft = tipFrac === null ? 0 : Math.min(0.94, Math.max(0.06, tipFrac));

  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-orientation="horizontal"
      aria-disabled={disabled || undefined}
      aria-valuemin={0}
      aria-valuemax={Math.max(0, Math.floor(duration))}
      aria-valuenow={Math.floor(dragging ? dragFrac * duration : value)}
      aria-valuetext={`${formatTime(dragging ? dragFrac * duration : value, locale)} / ${formatTime(duration, locale)}`}
      data-dragging={active}
      dir="ltr"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => finish(e, true)}
      onPointerCancel={(e) => finish(e, false)}
      onPointerLeave={() => !dragging && setHoverFrac(null)}
      onKeyDown={onKeyDown}
      // 28px tall invisible hit area straddling the bar's top edge.
      className="group absolute inset-x-0 -top-3.5 h-7 touch-none select-none outline-none"
      style={{ cursor: disabled ? "default" : "pointer" }}
    >
      {/* visible track: 3px → 6px on hover/drag */}
      <div
        className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-current/15 transition-[height] duration-150 group-hover:h-1.5 group-focus-visible:h-1.5 group-data-[dragging=true]:h-1.5"
      >
        <div
          className="h-full rounded-full bg-current"
          style={{ width: `${shownFrac * 100}%` }}
        />
      </div>

      {/* thumb: hidden → small dot on hover/focus → bigger while dragging */}
      <div
        aria-hidden
        className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 scale-0 rounded-full bg-current shadow transition-transform duration-150 group-hover:scale-100 group-focus-visible:scale-100 group-data-[dragging=true]:scale-150"
        style={{ left: `${shownFrac * 100}%`, opacity: disabled ? 0 : 1 }}
      />

      {/* time tooltip following the pointer / drag */}
      {tipFrac !== null && !disabled && duration > 0 && (
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-full mb-1 -translate-x-1/2 rounded-md bg-foreground px-1.5 py-0.5 text-xs font-medium tabular-nums text-background shadow"
          style={{ left: `${tipLeft * 100}%` }}
        >
          {formatTime(tipFrac * duration, locale)}
        </div>
      )}
    </div>
  );
}
